import { computed, makeObservable } from 'mobx';

import {
  ChannelKind,
  ChannelWidgetServer,
  IChannelBase,
  IWidgetChannelModel,
  normalizeBaseChannel,
  ChannelStatus
} from 'shared/entities/channels';
import { IRootStore } from 'shared/entities/store/rootStore';
import { AppNotificationType } from 'shared/entities/appNotifications';
import ChannelTokenModel from 'shared/models/channel/ChannelTokenModel';
import { FieldModel } from 'shared/models/form';
import { StatusType } from 'shared/newEntities/components/Status';
import { generateWidgetSnippet } from 'shared/utils/widgetScriptInjection';

import ListModel from '../../ListModel';
import ChannelBaseModel from '../ChannelBaseModel';

enum NotificationKey {
  pending = 'pending',
  error = 'error',
  ok = 'ok'
}

export default class WidgetChannelModel
  extends ChannelBaseModel
  implements IWidgetChannelModel
{
  readonly kind: ChannelKind.WIDGET = ChannelKind.WIDGET;
  readonly apiKey: FieldModel;
  readonly status: ChannelStatus;

  constructor({
    rootStore,
    ...rest
  }: Omit<IChannelBase, 'tokens' | 'scenarioIds'> & {
    rootStore: IRootStore;
    tokens: ListModel<ChannelTokenModel>;
    scenarioIds: FieldModel<string[]>;
    status: ChannelStatus;
  }) {
    super({
      ...rest,
      rootStore
    });

    makeObservable(this, {
      generatedSnippet: computed,
      channelStatus: computed,
      isChecking: computed,
      isInstallCheckBlocked: computed
    });

    this.apiKey = new FieldModel<string>(rest.externalId);
    this.status = rest.status;
  }

  get generatedSnippet(): string {
    return generateWidgetSnippet(this.externalId);
  }

  get channelStatus(): StatusType {
    if (this.status === ChannelStatus.ok) {
      return StatusType.success;
    }
    if (this.status === ChannelStatus.pending) {
      return StatusType.warning;
    }

    return StatusType.error;
  }

  get isChecking(): boolean {
    return this.rootStore.channelsStore.checkingWidgetChannelId === this.id;
  }

  get isInstallCheckBlocked(): boolean {
    return this.rootStore.channelsStore.checkingWidgetChannelId !== null;
  }

  checkInstall = async (): Promise<boolean> => {
    const { channelsStore } = this.rootStore;

    if (channelsStore.checkingWidgetChannelId !== null || !this.id) {
      return false;
    }

    channelsStore.setCheckingWidgetChannelId(this.id);

    const response = await channelsStore.load();

    if (response.isError) {
      channelsStore.setCheckingWidgetChannelId(null);
      this._showInstallNotification(
        AppNotificationType.error,
        NotificationKey.error
      );
      return false;
    }

    const freshChannel = channelsStore.channels.getEntity(this.id);

    channelsStore.setCheckingWidgetChannelId(null);

    if (!freshChannel || freshChannel.kind !== ChannelKind.WIDGET) {
      this._showInstallNotification(
        AppNotificationType.error,
        NotificationKey.error
      );
      return false;
    }

    if (freshChannel.status === ChannelStatus.ok) {
      this._showInstallNotification(
        AppNotificationType.success,
        NotificationKey.ok
      );
      return true;
    }

    if (freshChannel.status === ChannelStatus.pending) {
      this._showInstallNotification(
        AppNotificationType.warning,
        NotificationKey.pending
      );
      return false;
    }

    this._showInstallNotification(
      AppNotificationType.error,
      NotificationKey.error
    );
    return false;
  };

  private _showInstallNotification(
    type: AppNotificationType,
    key: NotificationKey
  ): void {
    this.rootStore.appNotificationsStore.open({
      type,
      title: (t) =>
        t(`ChannelsModal.ChannelForm.Widget.Install.notification.${key}`, {
          ns: 'newContainers'
        })
    });
  }

  static fromJson(
    raw: ChannelWidgetServer,
    rootStore: IRootStore
  ): WidgetChannelModel {
    const collection = this.getCollectionTokens(raw, rootStore);

    return new WidgetChannelModel({
      ...normalizeBaseChannel(raw),
      linked: raw.manager_linked,
      status: raw.status,
      tokens: new ListModel<ChannelTokenModel>(collection),
      scenarioIds: new FieldModel<string[]>(raw.scenario_ids),
      rootStore
    });
  }
}
